import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useSetRecoilState } from "recoil";
import { currentPostAtom } from "../../atom";

import { getCategoryInfo } from "../../api/categoryAPI";
import { getPostList, createPost, getPostData, postPost, getConferencePosts } from "../../api/postAPI";
import { createMediaGroup } from "../../api/mediaGroupAPI";
import { getLoginUser } from "../../api/user";
import { getBoardsWithRootCategory } from "../../api/boardApi";
import CategoryPermissionWidget from "./CategoryPermissionWidget";
import { ACCESS_TOKEN } from "../../lib/cookie";
import { createSlideUpload } from "../../lib/tusUpload";
import FileUploadInput from "../upload/FileUploadInput";
import { useSlideQC } from "../../hooks/useSlideQC";
import { SlideQCModal } from "../upload/SlideQCModal";

import styled from "styled-components";
import PostMediaCounts from "./PostMediaCounts";
import { Spin, Empty, Modal, Form, Input, Switch, Button, message, notification, Select } from "antd";
import UploadProgressModal from "../upload/UploadProgressModal";
import { isUploadCancelled, useTusCancel } from "../../hooks/useTusCancel";
import { PlusOutlined, LockOutlined, DownOutlined } from "@ant-design/icons";

const HEAVY_PROCESS_EXTENSIONS = ['zip', 'mrxs', 'ndpi'];

interface Props {
  rootCategoryId: number;
}

interface ProcessStatus {
  total: number;
  remaining: number;
}

// 서브 카테고리 + 해당 서브의 포스트 리스트
interface SubWithPosts {
  id: number;
  name: string;
  listOrder: number;
  posts: any[];
  [key: string]: any;
}

// 메인 카테고리에 대한 데이터(서브 리스트 + 메인 직속 포스트)
interface MainData {
  subs: SubWithPosts[];   // 서브 카테고리 (있을 수도, 없을 수도)
  mainPosts: any[];        // 메인 카테고리에 직접 달린 포스트
}

const CategoryBoardNoSub = ({ rootCategoryId }: Props) => {
  const [mainCategories, setMainCategories] = useState<any[]>([]);
  const [activeMainId, setActiveMainId] = useState<number | null>(null);

  const [dataMap, setDataMap] = useState<Record<number, MainData>>({});

  const [showSlideUploadModal, setShowSlideUploadModal] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>("");
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<Record<string, ProcessStatus>>({});
  const { showQCModal, qcItems, qcDone, qcHasIssue, runQC, closeQCModal } = useSlideQC();
  const tusCancel = useTusCancel();

  /** 전송하지 못한 파일 수만큼 "처리 중" 카운트를 되돌린다 (안 하면 게시물이 계속 처리 중으로 잠김) */
  const releaseProcessingStatus = (postId: number, unsentCount: number) => {
    if (unsentCount <= 0) return;
    setProcessingStatus(prev => {
      const current = prev[postId];
      if (!current) return prev;
      return {
        ...prev,
        [postId]: {
          total: Math.max(0, current.total - unsentCount),
          remaining: Math.max(0, current.remaining - unsentCount),
        },
      };
    });
  };
  const [slideNames, setSlideNames] = useState<Record<string, string>>({});
  const [fileList, setFileList] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [expandedSubs, setExpandedSubs] = useState<Record<string, boolean>>({});

  const setCurrentPost = useSetRecoilState(currentPostAtom);
  const navigate = useNavigate();

  // 카테고리 권한 위젯: 어드민 && 카테고리별 권한이 켜진 게시판일 때만 노출
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isCategoryPermBoard, setIsCategoryPermBoard] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await getLoginUser();
        const admin = user?.role === "ADMIN_MAIN" || user?.role === "ADMIN_SUB";
        if (cancelled) return;
        setIsAdmin(admin);
        if (!admin) return;

        // rootCategories/권한 플래그를 포함한 게시판 목록으로, 이 rootCategory 를 가진 게시판이
        // 카테고리별 권한(isCategoryPermissionEnabled)을 사용하는지 확인
        const boardsRes = await getBoardsWithRootCategory();
        const boards: any[] = Array.isArray(boardsRes?.data) ? boardsRes.data : [];
        const board = boards.find((b) =>
          (b.rootCategories ?? []).some((rc: any) => Number(rc.id) === Number(rootCategoryId))
        );
        if (cancelled) return;
        setIsCategoryPermBoard(
          !!board?.isPermissionEnabled && !!board?.isCategoryPermissionEnabled
        );
      } catch (e) {
        console.error("권한 위젯 노출 판별 실패:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rootCategoryId]);

  const showPermissionWidget = isAdmin && isCategoryPermBoard;

  const [form] = Form.useForm();
  const [useGroupId, setUseGroupId] = useState<boolean>(false);

  const [uploadTargetCategoryId, setUploadTargetCategoryId] = useState<number | null>(null);
  const [uploadTargetOptions, setUploadTargetOptions] = useState<{ label: string; value: number }[]>([]);

  //학술강좌 post 추가용
  const [conferencePostMap, setConferencePostMap] = useState<Record<number, number[]>>({});
  const [conferencePostMapLoaded, setConferencePostMapLoaded] = useState(false);

  const updateParams = (mainId: number, categoryName?: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("category", String(mainId));
    if (categoryName) {
      newParams.set("categoryName", categoryName);
    }
    setSearchParams(newParams, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortByNumberPrefix = (posts: any[]) => {
  return [...posts].sort((a, b) => {
    const getNum = (title: string) => {
      const match = (title || "").match(/^(\d+)\./);
      return match ? parseInt(match[1], 10) : Infinity;
    };
    return getNum(a.title ?? a.name) - getNum(b.title ?? b.name);
  });
};

  const fetchMainData = async (mainId: number, forceUpdate = false) => {
  setActiveMainId(mainId);
  if (!forceUpdate && dataMap[mainId]) return;

  setContentLoading(true);
  try {
    // 서브 카테고리는 최초 getCategoryInfo(rootCategoryId) 트리에 이미 포함되어 있어 재조회하지 않고
    // mainCategories 에서 꺼내 쓴다. (메인 직속 게시글만 별도 조회)
    const mainNode = mainCategories.find((c) => c.id === mainId);
    const subs = mainNode?.children ?? [];
    const mainPostsRaw = await getPostList(1, 9999, mainId, "id", "DESC");

    const conferencePostIds = conferencePostMap[mainId] || [];
    const conferencePostsData = await Promise.all(
      conferencePostIds.map((postId) => getPostData(postId))
    );

    const mainPosts = mainPostsRaw?.length
      ? [...mainPostsRaw].sort((a: any, b: any) => a.listOrder - b.listOrder)
      : [];

    const existingIds = new Set(mainPosts.map((p: any) => p.id));
    const newConferencePosts = conferencePostsData.filter(
      (p) => p && !existingIds.has(p.id)
    );
    const mergedMainPosts = [...mainPosts, ...newConferencePosts];

    let subsWithPosts: SubWithPosts[] = [];
    if (subs?.length) {
      const sortedSubs = [...subs].sort((a: any, b: any) => a.listOrder - b.listOrder);
      subsWithPosts = await Promise.all(
        sortedSubs.map(async (sub: any) => {
          const postListData = await getPostList(1, 9999, sub.id, "id", "DESC");
          const sortedPosts = postListData?.length
            ? [...postListData].sort((a: any, b: any) => a.listOrder - b.listOrder)
            : [];

          const subConferenceIds = conferencePostMap[sub.id] || [];
          const subConferencePosts = await Promise.all(
            subConferenceIds.map((postId) => getPostData(postId))
          );
          const existingSubIds = new Set(sortedPosts.map((p: any) => p.id));
          const newSubConferencePosts = subConferencePosts.filter(
            (p) => p && !existingSubIds.has(p.id)
          );
          const mergedSubPosts = [...sortedPosts, ...newSubConferencePosts];

          return {
            ...sub,
            posts: newSubConferencePosts.length > 0
              ? sortByNumberPrefix(mergedSubPosts)
              : sortedPosts,
          };
        })
      );
    }

    setDataMap((prev) => ({
      ...prev,
      [mainId]: { subs: subsWithPosts, mainPosts: mergedMainPosts },
    }));
  } catch (error) {
    console.error(error);
  } finally {
    setContentLoading(false);
  }
};

  const toggleSub = (mainId: number, subId: number) => {
    const key = `${mainId}-${subId}`;
    setExpandedSubs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAddPostClick = () => {
    if (!activeMainId) return;
    const data = dataMap[activeMainId];
    const activeCategory = mainCategories.find(c => c.id === activeMainId);
    const options: { label: string; value: number }[] = [];
    if (activeCategory) {
      options.push({ label: `${activeCategory.name} (메인)`, value: activeCategory.id });
    }
    if (data?.subs?.length) {
      data.subs.forEach((s) => options.push({ label: s.name, value: s.id }));
    }
    setUploadTargetOptions(options);
    setUploadTargetCategoryId(activeMainId);
    form.setFieldValue('targetCategoryId', activeMainId);
    setShowSlideUploadModal(true);
  };

  const uploadFile = async (
    postId: number,
    file: any,
    fileIndex: number,
    totalFileCount: number,
    tracker: { successCount: number; notified: boolean },
    postTitle: string,
    hasHeavyFile: boolean,
    slideName: string = '',
  ) => {
    setUploadingFileName(file.name);
    setCurrentFileIndex(fileIndex + 1);

    return new Promise<void>((resolve, reject) => {
      let isResolved = false;

      const upload = createSlideUpload(file, {
        endpoint: process.env.REACT_APP_BACKEND_URL + "/api/slide/tus",
        headers: { Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}` },
        chunkSize: 1024 * 1024 * 5,
        // 이어올리기를 쓰지 않으므로 이어올리기 정보도 저장하지 않는다
        // (남기면 AddSlide 에서 같은 파일을 올릴 때 이 업로드의 게시물로 이어올려질 수 있다)
        storeFingerprintForResuming: false,
        metadata: {
          postId: String(postId),
          filename: file.name || "",
          slidename: slideName,
          caption: "",
          gender: "",
          age: "",
          site: "",
          zindanName: "",
        },
        onError: reject,
        onProgress: function (bytesUploaded, bytesTotal) {
          const currentFilePercentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          setCurrentProgress(Number(currentFilePercentage));

          const completedFiles = fileIndex;
          const overallPercentage = ((completedFiles + (bytesUploaded / bytesTotal)) / totalFileCount * 100).toFixed(2);
          setTotalProgress(Number(overallPercentage));

          if (bytesUploaded === bytesTotal) {
            if (!isResolved) {
              isResolved = true;
              tusCancel.markSent(upload);
              setTimeout(() => {
                resolve();
              }, 500);
            }
          }
        },
        onSuccess: async () => {
          tracker.successCount++;
          console.log(`[Server] ${file.name} processing finished.`);

          setProcessingStatus(prev => {
            const currentStatus = prev[postId] || { total: 0, remaining: 0 };
            const newRemaining = currentStatus.remaining > 0 ? currentStatus.remaining - 1 : 0;

            return {
              ...prev,
              [postId]: { ...currentStatus, remaining: newRemaining }
            };
          });

          if (tracker.successCount === totalFileCount && !tracker.notified) {
            tracker.notified = true;
            console.log("All files processed.");

            if (activeMainId) await fetchMainData(activeMainId, true);

            if (hasHeavyFile) {
              notification.success({
                key: `upload-complete-${postId}`,
                message: '처리 완료',
                description: `'${postTitle}'의 모든 파일 처리가 완료되어 조회가 가능합니다.`,
                duration: 5,
              });
            }
          }
        }
      }, { isCancelled: tusCancel.isCancelled });
      tusCancel.track(upload, reject);
      upload.start();
    });
  };

  const handleFileUpload = async (values: any) => {
    // 중지·오류 처리에서 쓰는 업로드 진행 정보
    let uploadPostId: number | null = null;
    let uploadTotalCount = 0;
    let sentCount = 0;
    try {
      const targetId = values.targetCategoryId ?? uploadTargetCategoryId;
      if (!targetId) {
        message.error("업로드 대상 카테고리가 지정되지 않았습니다.");
        return;
      }

      const postId = await createPost(Number(targetId));
      if (!postId) throw new Error('Failed to create post');
      uploadPostId = postId;
      await postPost(postId, values.title, null);

      if (values.file) {
        const fileList = values.file.fileList;
        const postTitle = values.title || "새 포스트";
        const groupName = useGroupId ? (values.groupName || "").trim() : "";

        const hasHeavyFile = fileList.some((item: any) => {
          const ext = item.name.split('.').pop()?.toLowerCase();
          return ext && HEAVY_PROCESS_EXTENSIONS.includes(ext);
        });

        setProcessingStatus(prev => ({
          ...prev,
          [postId]: { total: fileList.length, remaining: fileList.length }
        }));

        setTotalFiles(fileList.length);
        setShowProgressModal(true);
        setCurrentProgress(0);
        setTotalProgress(0);
        setCurrentFileIndex(0);
        tusCancel.begin();
        uploadTotalCount = fileList.length;

        const tracker = { successCount: 0, notified: false };

        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i];
          const slideName = slideNames[file.uid] || '';
          tusCancel.throwIfCancelled();
          await uploadFile(postId, file.originFileObj, i, fileList.length, tracker, postTitle, hasHeavyFile, slideName);
          sentCount++;
        }

        // 새로 만든 게시글이라 업로드된 슬라이드 전부가 대상.
        // 그룹은 /api/media-groups 로 생성하면서 한 번에 배정한다 (TUS 메타데이터 groupId 대체).
        if (groupName) {
          try {
            const post = await getPostData(postId);
            const slideIds = (post?.slides ?? []).map((s: any) => s.slideId).filter(Boolean);
            if (slideIds.length > 0) {
              await createMediaGroup({
                postId,
                mediaType: "SLIDE",
                name: groupName,
                mediaIds: slideIds,
              });
            }
          } catch (e: any) {
            console.error("uploaded slide group assign error:", e);
            message.error(e?.message || "업로드는 완료됐지만 그룹 배정에 실패했습니다.");
          }
        }

        setShowProgressModal(false);
        setShowSlideUploadModal(false);
        form.resetFields();
        setUseGroupId(false);
        setSlideNames({});
        setFileList([]);
        runQC(postId, new Set(), fileList.map((item: any) => item.originFileObj?.name || item.name));

        if (hasHeavyFile) {
          notification.info({
            message: '대용량 파일 전송 완료',
            description: '파일 전송은 완료되었으나, 서버 처리에 시간이 소요될 수 있습니다.',
            duration: 5,
            placement: 'bottomRight',
          });
        } else {
          message.success("포스트 작성이 완료되었습니다.");
        }
      } else {
        setShowSlideUploadModal(false);
        form.resetFields();
        setUseGroupId(false);
        setSlideNames({});
        setFileList([]);
      }
    } catch (error) {
      setShowProgressModal(false);
      setShowSlideUploadModal(false);
      setSlideNames({});
      setFileList([]);
      if (uploadPostId != null) releaseProcessingStatus(uploadPostId, uploadTotalCount - sentCount);

      if (isUploadCancelled(error)) {
        // 중지: 게시물과 이미 전송된 파일은 그대로 둔다 (자동 삭제 X). 그룹 생성·QC 는 하지 않는다
        form.resetFields();
        setUseGroupId(false);
        notification.warning({
          message: "업로드를 중지했습니다",
          description:
            (sentCount > 0
              ? `전체 ${uploadTotalCount}개 중 전송이 끝난 ${sentCount}개 파일은 등록됩니다.`
              : "등록된 파일은 없습니다.") +
            " 게시물은 이미 만들어져 있으니 필요 없으면 삭제해주세요.",
          duration: 8,
          placement: "bottomRight",
        });
        return;
      }
      console.error("Upload error:", error);
      message.error("업로드 중 오류가 발생했습니다.");
    } finally {
      if (activeMainId) {
        fetchMainData(activeMainId, true);
      }
    }
  };

  const handlePostNavigate = async (post: any, sectionLabel: string) => {
    setCurrentPost(await getPostData(post.id));
    const params = new URLSearchParams();
    params.set('title', post.title || post.name || '제목 없음');
    const boardParam = searchParams.get('board');
    if (boardParam) {
      params.set('board', boardParam);
    }
    if (sectionLabel) {
      params.set('categoryName', sectionLabel);
    }
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      params.set('category', categoryParam);
    }
    navigate(`/post/${post.id}?${params.toString()}`);
  };

  useEffect(() => {
    getConferencePosts().then(res => {
      if (res.success && res.data) {
        const map: Record<number, number[]> = {};
        res.data.forEach((item: { conferencePostId: number; postId: number; categoryId: number }) => {
          if (!map[item.categoryId]) map[item.categoryId] = [];
          map[item.categoryId].push(item.postId);
        });
        setConferencePostMap(map);
      }
      setConferencePostMapLoaded(true);
    });
  }, []);

  useEffect(() => {
    const fetchMain = async () => {
      setLoading(true);
      try {
        // getCategoryInfo 는 root + 메인 + 서브를 한 번에 트리로 내려주므로 메인 목록은 children 에서 취한다.
        const info = await getCategoryInfo(rootCategoryId);
        const data = info?.children ?? [];
        data.sort((a: any, b: any) => a.listOrder - b.listOrder);
        setMainCategories(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchMain();
  }, [rootCategoryId]);

  useEffect(() => {
    if (mainCategories.length === 0) return;
    const categoryParam = searchParams.get("category");

    if (categoryParam) {
      const targetId = Number(categoryParam);
      fetchMainData(targetId);
    } else if (mainCategories.length > 0) {
      const firstCategory = mainCategories[0];
      const newParams = new URLSearchParams(searchParams);
      newParams.set("category", String(firstCategory.id));
      newParams.set("categoryName", firstCategory.name);
      setSearchParams(newParams, { replace: true });
    }
  }, [mainCategories, conferencePostMapLoaded]);

  useEffect(() => {
    if (mainCategories.length === 0) return;
    const categoryParam = searchParams.get("category");

    if (categoryParam) {
      const targetId = Number(categoryParam);
      fetchMainData(targetId);
    }
  }, [searchParams]);

  if (loading) return <Center><Spin size="large" /></Center>;

  if (mainCategories.length === 0) {
    return (
      <StCategoryContainer>
        <Center style={{ height: '60vh' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: '16px', color: '#888' }}>
                현재 게시판 준비중입니다.
              </span>
            }
          />
        </Center>
      </StCategoryContainer>
    );
  }

  const activeCategory = mainCategories.find(c => c.id === activeMainId);
  const activeData = activeMainId ? dataMap[activeMainId] : undefined;
  const hasSubs = !!activeData && activeData.subs.length > 0;

  // 게시글 수(postCnt) + 슬라이드 수(slideCnt)를 나란히 표기
  const renderCounts = (postCnt?: number, slideCnt?: number) => (
    <>
      <span className="cnt">Post: <b>{postCnt ?? 0}</b></span>
      <span className="cnt">Image: <b>{slideCnt ?? 0}</b></span>
    </>
  );

  return (
    <StCategoryContainer>
      <HalfLayout>
        <SideMenu>
          {mainCategories.map((main, index) => {
            return (
              <MainMenuItem
                key={main.id}
                $active={activeMainId === main.id}
                onClick={() => updateParams(main.id, main.name)}
              >
                <div className="title-row">
                  <span className="index">{index + 1}.</span>
                  <span className="text">{main.name}</span>
                </div>
                <CountBadge $active={activeMainId === main.id}>
                  {renderCounts(main.postCnt, main.slideCnt)}
                </CountBadge>
              </MainMenuItem>
            );
          })}
        </SideMenu>

        <ContentArea>
          {contentLoading ? (
            <Center><Spin tip="Loading contents..." /></Center>
          ) : activeMainId && activeData ? (
            <>
              <HeaderRow>
                <SubTitle>{activeCategory?.name || "Category"}</SubTitle>
                <StAddButton onClick={handleAddPostClick}>
                  <PlusOutlined style={{ fontSize: '12px' }} /> Add Post
                </StAddButton>
              </HeaderRow>

              {!hasSubs ? (
                <SingleSection>
                  <PostList>
                    {activeData.mainPosts.length > 0 ? (
                      activeData.mainPosts.map((post: any) => (
                        <PostLink
                          key={post.id}
                          onClick={() => handlePostNavigate(post, activeCategory?.name || "")}
                        >
                          {post.isSecret && (
                            <LockOutlined style={{ marginRight: '6px', color: '#bfbfbf' }} />
                          )}
                          {post.title || post.name || "제목 없음"}
                          <PostMediaCounts post={post} />
                        </PostLink>
                      ))
                    ) : (
                      <Empty style={{ marginTop: 60 }} description="게시글이 없습니다." />
                    )}
                  </PostList>
                </SingleSection>
              ) : (
                <>
                  {activeData.mainPosts.length > 0 && (
                    <SubSection>
                      <PlainPostListWrapper>
                        <PostList>
                          {activeData.mainPosts.map((post: any) => (
                            <PostLink
                              key={post.id}
                              onClick={() => handlePostNavigate(post, activeCategory?.name || "")}
                            >
                              {post.isSecret && (
                                <LockOutlined style={{ marginRight: '6px', color: '#bfbfbf' }} />
                              )}
                              {post.title || post.name || "제목 없음"}
                              <PostMediaCounts post={post} />
                            </PostLink>
                          ))}
                        </PostList>
                      </PlainPostListWrapper>
                    </SubSection>
                  )}

                  {activeData.subs.map((sub) => {
                    const isExpanded = !!expandedSubs[`${activeMainId}-${sub.id}`];
                    return (
                      <SubSection key={sub.id}>
                        <SubHeader onClick={() => toggleSub(activeMainId, sub.id)}>
                          <SubHeaderLeft>
                            <ChevronIcon $expanded={isExpanded}>
                              <DownOutlined />
                            </ChevronIcon>
                            <SubTitle>{sub.name}</SubTitle>
                            <SubCount>{renderCounts(sub.postCnt, sub.slideCnt)}</SubCount>
                          </SubHeaderLeft>
                        </SubHeader>

                        <PostListWrapper $expanded={isExpanded} $count={sub.posts.length}>
                          <SubPostList>
                            {sub.posts.map((post: any) => (
                              <PostLink
                                key={post.id}
                                onClick={() => handlePostNavigate(post, sub.name)}
                              >
                                {post.isSecret && (
                                  <LockOutlined style={{ marginRight: '6px', color: '#bfbfbf' }} />
                                )}
                                {post.title || post.name || "제목 없음"}
                                <PostMediaCounts post={post} />
                              </PostLink>
                            ))}
                          </SubPostList>
                        </PostListWrapper>
                      </SubSection>
                    );
                  })}
                </>
              )}
            </>
          ) : null}
        </ContentArea>

        {/* 어드민 && 카테고리별 권한 게시판일 때: 현재 카테고리의 권한 관리 위젯 (우측) */}
        {showPermissionWidget && activeMainId && (
          <CategoryPermissionWidget
            categoryId={activeMainId}
            categoryName={activeCategory?.name}
          />
        )}
      </HalfLayout>

      <Modal
        open={showSlideUploadModal}
        onCancel={() => {
          form.resetFields();
          setUseGroupId(false);
          setSlideNames({});
          setFileList([]);
          setShowSlideUploadModal(false);
        }}
        centered
        footer={null}
        width="40%"
      >
        <Form form={form} onFinish={handleFileUpload} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input />
          </Form.Item>

          {/* 서브 카테고리가 있으면 업로드 대상 선택 노출 */}
          {uploadTargetOptions.length > 1 && (
            <Form.Item
              name="targetCategoryId"
              label="카테고리 선택"
              rules={[{ required: true, message: "카테고리를 선택해주세요" }]}
            >
              <Select options={uploadTargetOptions} />
            </Form.Item>
          )}

          {/* 새 게시글이라 기존 그룹이 없다. 업로드 후 이 이름으로 그룹을 만들어 전부 배정한다. */}
          <Form.Item label="그룹 지정" style={{ marginBottom: useGroupId ? '8px' : '16px' }}>
            <Switch checked={useGroupId} onChange={(checked) => {
              setUseGroupId(checked);
              if (!checked) form.setFieldValue('groupName', undefined);
            }} />
          </Form.Item>
          {useGroupId && (
            <Form.Item
              name="groupName"
              label="그룹 이름"
              rules={[{ required: true, message: "그룹 이름을 입력해주세요." }]}
            >
              <Input placeholder="업로드한 슬라이드를 묶을 그룹 이름" maxLength={255} />
            </Form.Item>
          )}
          <Form.Item
            name="file"
            label="File"
            style={{ marginBottom: "0" }}
          >
            <FileUploadInput
              mode="slide"
              multiple={true}
              showSuccessMessage={false}
              onChange={(val) => setFileList(val?.fileList || [])}
            />
          </Form.Item>
          {fileList.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>슬라이드 이름 입력</div>
              {fileList.map((item: any) => (
                <div key={item.uid} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                  <span style={{ flex: '0 0 auto', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#595959', fontSize: '12px' }}>
                    {item.name}
                  </span>
                  <Input
                    placeholder="슬라이드 이름 (선택)"
                    value={slideNames[item.uid] || ''}
                    onChange={(e) => setSlideNames(prev => ({ ...prev, [item.uid]: e.target.value }))}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </div>
          )}
          <Form.Item style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => {
                form.resetFields();
                setUseGroupId(false);
                setSlideNames({});
                setFileList([]); 
                setShowSlideUploadModal(false);
              }} style={{ marginRight: "10px" }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <UploadProgressModal
        open={showProgressModal}
        fileName={uploadingFileName}
        fileIndex={currentFileIndex}
        totalFiles={totalFiles}
        currentPercent={currentProgress}
        totalPercent={totalProgress}
        onCancel={tusCancel.cancel}
        cancelling={tusCancel.cancelling}
      />

      <SlideQCModal
        open={showQCModal}
        items={qcItems}
        done={qcDone}
        hasIssue={qcHasIssue}
        onClose={closeQCModal}
      />
    </StCategoryContainer>
  );
};

const StCategoryContainer = styled.div`
  width: 95%;
  max-width: 1800px;
  margin: 3rem auto;
`;

const HalfLayout = styled.div`
  display: flex;
  gap: 48px;
  align-items: flex-start;
`;

const SideMenu = styled.div`
  width: 380px;
  flex-shrink: 0;
  position: sticky;
  top: 180px;
  align-self: flex-start;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: #f8fafc; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

const ContentArea = styled.div`
  flex: 2;
  min-height: 600px;
  padding-top: 5px;
`;

const MainMenuItem = styled.div<{ $active: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  background: ${(p) => (p.$active ? "#f0f6fd" : "#fff")};
  transition: background 0.15s;

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${(p) => (p.$active ? "#eff6ff" : "#f8fafc")};
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    font-weight: ${(p) => (p.$active ? 600 : 400)};
    color: ${(p) => (p.$active ? "#1e4d8c" : "#374151")};
  }
`;

const CountBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 2px;
  background: ${(p) => (p.$active ? "#ffffff" : "#f6f6f6")};
  color: ${(p) => (p.$active ? "#5386ca" : "#64748b")};
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.15s;

  .cnt {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  b {
    font-weight: 700;
  }
`;

const SingleSection = styled.div`
  margin-bottom: 35px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-right: 10px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
`;

const SubTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  min-width: 0;
`;

const SubCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f6f6f6;
  padding: 2px 8px;
  border-radius: 2px;
  flex-shrink: 0;

  .cnt {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  b {
    font-weight: 700;
  }
`;

const StAddButton = styled.button`
  background: transparent;
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;

  &:hover {
    color: #1890ff;
    border-color: #1890ff;
    background-color: #e6f7ff;
  }
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 15px;
  margin-top: 15px;
`;

const SubPostList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 18px 14px 46px;
  gap: 2px;
  border-top: 1px solid #f1f5f9;
`;

const PostLink = styled.div`
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  padding: 7px 4px;
  border-radius: 4px;
  line-height: 1.5;
  transition: color 0.15s, background 0.15s;
  display: flex;
  align-items: center;

  &::before {
    content: "–";
    margin-right: 10px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  &:hover {
    color: #1e4d8c;
    background: #f8fcff;
    padding-left: 8px;
  }
`;

const SubSection = styled.div`
  margin-bottom: 6px;
  overflow: hidden;
  background: #fff;
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  background: #fff;
  transition: background 0.15s;
  user-select: text; /* 텍스트 드래그 복사 허용 (클릭 펼침은 그대로 동작) */
  gap: 10px;

  &:hover { background: #f8fafc; }
`;

const SubHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const ChevronIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${(p) => (p.$expanded ? "rotate(0deg)" : "rotate(-90deg)")};
  flex-shrink: 0;
`;

const PostListWrapper = styled.div<{ $expanded: boolean; $count: number }>`
  max-height: ${(p) => (p.$expanded ? `${p.$count * 44 + 20}px` : "0px")};
  overflow: hidden;
  transition: max-height 0.25s ease;
`;

const PlainPostListWrapper = styled.div`
  padding-bottom: 8px;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
  flex: 1;
`;

export default CategoryBoardNoSub;